import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

var API_URL = process.env.REACT_APP_API_URL || 'https://ivision-crm-backend-production.up.railway.app';

function TVDisplay() {
  var { subdomain } = useParams();
  var [data, setData] = useState(null);
  var [error, setError] = useState(null);
  var [time, setTime] = useState(new Date());

  var loadData = async function() {
    try {
      var res = await fetch(API_URL + '/api/public/tv/' + subdomain);
      var json = await res.json();
      if (json.success) setData(json);
      else setError('Business not found');
    } catch (err) {
      setError('Unable to connect');
    }
  };

  useEffect(function() {
    loadData();
    var dataInterval = setInterval(loadData, 30000); // refresh every 30s
    var clockInterval = setInterval(function() { setTime(new Date()); }, 1000);
    return function() { clearInterval(dataInterval); clearInterval(clockInterval); };
  }, [subdomain]);

  if (error) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', paddingTop: '20vh' }}>
          <div style={{ fontSize: 60, marginBottom: 20 }}>📺</div>
          <div style={{ fontSize: 28, color: '#fff', fontWeight: 700 }}>{error}</div>
          <div style={{ fontSize: 16, color: '#888', marginTop: 10 }}>Check the URL and try again</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', paddingTop: '20vh' }}>
          <div style={{ fontSize: 28, color: '#888' }}>Loading...</div>
        </div>
      </div>
    );
  }

  var biz = data.business;
  var primary = biz.primaryColor || '#ff6b2b';
  var bookings = data.bookings || [];
  var now = time.toTimeString().slice(0, 5); // HH:MM

  // Split bookings into current/next and upcoming
  var nextUp = null;
  var upcoming = [];
  var inProgress = [];

  bookings.forEach(function(b) {
    if (b.status === 'in_progress' || b.status === 'checked_in') {
      inProgress.push(b);
    } else if (!nextUp && b.time >= now) {
      nextUp = b;
    } else if (b.time >= now) {
      upcoming.push(b);
    }
  });

  var formatTime12 = function(t) {
    if (!t) return '';
    var parts = t.split(':');
    var h = parseInt(parts[0]);
    var m = parts[1];
    var ampm = h >= 12 ? 'PM' : 'AM';
    if (h > 12) h -= 12;
    if (h === 0) h = 12;
    return h + ':' + m + ' ' + ampm;
  };

  var formatClock = function() {
    var h = time.getHours();
    var m = time.getMinutes().toString().padStart(2, '0');
    var ampm = h >= 12 ? 'PM' : 'AM';
    if (h > 12) h -= 12;
    if (h === 0) h = 12;
    return h + ':' + m + ' ' + ampm;
  };

  var formatDate = function() {
    return time.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  var statusColor = function(status) {
    if (status === 'in_progress' || status === 'checked_in') return '#10b981';
    if (status === 'confirmed') return primary;
    return '#6b7280';
  };

  var statusLabel = function(status) {
    if (status === 'in_progress') return 'IN PROGRESS';
    if (status === 'checked_in') return 'CHECKED IN';
    if (status === 'confirmed') return 'CONFIRMED';
    if (status === 'pending') return 'PENDING';
    return status.toUpperCase();
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={{ ...styles.header, borderBottom: '3px solid ' + primary }}>
        <div style={styles.headerLeft}>
          {biz.logoUrl ? (
            <img src={biz.logoUrl} alt="" style={{ height: 50, borderRadius: 10 }} />
          ) : (
            <div style={{ ...styles.logoBox, background: 'linear-gradient(135deg, ' + primary + ', ' + (biz.secondaryColor || '#ffab40') + ')' }}>
              <span style={{ fontSize: 24, fontWeight: 900, color: '#fff' }}>
                {biz.name.charAt(0)}
              </span>
            </div>
          )}
          <div>
            <div style={styles.bizName}>{biz.name}</div>
            <div style={styles.bizDate}>{formatDate()}</div>
          </div>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.clock}>{formatClock()}</div>
          <div style={styles.bookingCount}>
            <span style={{ ...styles.countBadge, background: primary }}>{data.totalBookings}</span>
            <span style={{ color: '#888', fontSize: 14 }}>appointments today</span>
          </div>
        </div>
      </div>

      <div style={styles.body}>
        {/* Left: Now Serving + Next Up */}
        <div style={styles.leftPanel}>
          {/* Now Serving */}
          {inProgress.length > 0 && (
            <div style={styles.nowServing}>
              <div style={styles.sectionLabel}>NOW SERVING</div>
              {inProgress.map(function(b) {
                return (
                  <div key={b.id} style={{ ...styles.bigCard, borderLeft: '6px solid #10b981' }}>
                    <div style={styles.bigCardCustomer}>{b.customer}</div>
                    <div style={styles.bigCardService}>{b.service}</div>
                    <div style={styles.bigCardMeta}>
                      <span>{formatTime12(b.time)}</span>
                      <span style={{ margin: '0 8px', opacity: 0.4 }}>•</span>
                      <span>with {b.staff}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Next Up */}
          {nextUp && (
            <div style={{ marginTop: inProgress.length > 0 ? 30 : 0 }}>
              <div style={styles.sectionLabel}>NEXT UP</div>
              <div style={{ ...styles.bigCard, borderLeft: '6px solid ' + primary }}>
                <div style={styles.bigCardCustomer}>{nextUp.customer}</div>
                <div style={styles.bigCardService}>{nextUp.service}</div>
                <div style={styles.bigCardMeta}>
                  <span>{formatTime12(nextUp.time)}</span>
                  <span style={{ margin: '0 8px', opacity: 0.4 }}>•</span>
                  <span>with {nextUp.staff}</span>
                </div>
              </div>
            </div>
          )}

          {inProgress.length === 0 && !nextUp && (
            <div style={{ textAlign: 'center', paddingTop: 60 }}>
              <div style={{ fontSize: 60, marginBottom: 16 }}>✨</div>
              <div style={{ fontSize: 24, color: '#fff', fontWeight: 600 }}>No more appointments</div>
              <div style={{ fontSize: 16, color: '#666', marginTop: 8 }}>Walk-ins welcome!</div>
            </div>
          )}
        </div>

        {/* Right: Today's Schedule */}
        <div style={styles.rightPanel}>
          <div style={styles.sectionLabel}>TODAY'S SCHEDULE</div>
          <div style={styles.scheduleList}>
            {bookings.length === 0 && (
              <div style={{ color: '#666', fontSize: 18, padding: 30, textAlign: 'center' }}>No appointments scheduled</div>
            )}
            {bookings.map(function(b) {
              var isPast = b.time < now && b.status !== 'in_progress' && b.status !== 'checked_in';
              return (
                <div key={b.id} style={{ ...styles.scheduleRow, opacity: isPast ? 0.4 : 1 }}>
                  <div style={styles.scheduleTime}>{formatTime12(b.time)}</div>
                  <div style={styles.scheduleDot}>
                    <div style={{ width: 10, height: 10, borderRadius: 5, background: statusColor(b.status) }} />
                  </div>
                  <div style={styles.scheduleInfo}>
                    <div style={styles.scheduleName}>{b.customer}</div>
                    <div style={styles.scheduleService}>{b.service} · {b.staff}</div>
                  </div>
                  <div style={{ ...styles.scheduleStatus, color: statusColor(b.status) }}>
                    {statusLabel(b.status)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Staff Summary */}
          {data.staff && data.staff.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div style={styles.sectionLabel}>TEAM TODAY</div>
              <div style={styles.staffRow}>
                {data.staff.filter(function(s) { return s.bookingCount > 0; }).map(function(s, i) {
                  return (
                    <div key={i} style={styles.staffChip}>
                      <div style={{ ...styles.staffAvatar, background: primary }}>{s.name.charAt(0)}</div>
                      <div>
                        <div style={{ fontWeight: 600, color: '#fff', fontSize: 14 }}>{s.name}</div>
                        <div style={{ color: '#888', fontSize: 12 }}>{s.bookingCount} apt{s.bookingCount !== 1 ? 's' : ''}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ ...styles.footer, borderTop: '2px solid ' + primary + '33' }}>
        <span style={{ color: '#555' }}>Powered by IVision Marketing</span>
        <span style={{ color: '#555' }}>Auto-refreshes every 30 seconds</span>
      </div>
    </div>
  );
}

var styles = {
  container: {
    minHeight: '100vh', background: '#1a1c2e', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '20px 40px', background: '#0f0f1e',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 20 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 30 },
  logoBox: {
    width: 50, height: 50, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  bizName: { fontSize: 28, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' },
  bizDate: { fontSize: 15, color: '#888', marginTop: 2 },
  clock: { fontSize: 42, fontWeight: 300, color: '#fff', fontVariantNumeric: 'tabular-nums' },
  bookingCount: { display: 'flex', alignItems: 'center', gap: 8 },
  countBadge: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 36, height: 36, borderRadius: 10, color: '#fff', fontSize: 18, fontWeight: 800,
  },
  body: {
    flex: 1, display: 'flex', gap: 0, overflow: 'hidden',
  },
  leftPanel: {
    flex: '0 0 40%', padding: '30px 40px', borderRight: '1px solid #252840',
    overflowY: 'auto',
  },
  rightPanel: {
    flex: 1, padding: '30px 40px', overflowY: 'auto',
  },
  sectionLabel: {
    fontSize: 12, fontWeight: 700, color: '#888', letterSpacing: 2, marginBottom: 16,
    textTransform: 'uppercase',
  },
  nowServing: {},
  bigCard: {
    background: '#12121f', borderRadius: 16, padding: '24px 28px', marginBottom: 12,
  },
  bigCardCustomer: { fontSize: 32, fontWeight: 700, color: '#fff' },
  bigCardService: { fontSize: 18, color: '#aaa', marginTop: 6 },
  bigCardMeta: { fontSize: 15, color: '#666', marginTop: 10 },
  scheduleList: {
    display: 'flex', flexDirection: 'column', gap: 2,
  },
  scheduleRow: {
    display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px',
    borderRadius: 10, transition: 'opacity 0.3s',
  },
  scheduleTime: {
    width: 80, fontSize: 14, fontWeight: 600, color: '#888', fontVariantNumeric: 'tabular-nums',
  },
  scheduleDot: { width: 20, display: 'flex', justifyContent: 'center' },
  scheduleInfo: { flex: 1 },
  scheduleName: { fontSize: 16, fontWeight: 600, color: '#fff' },
  scheduleService: { fontSize: 13, color: '#888', marginTop: 2 },
  scheduleStatus: { fontSize: 11, fontWeight: 700, letterSpacing: 1 },
  staffRow: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  staffChip: {
    display: 'flex', alignItems: 'center', gap: 10, background: '#12121f',
    borderRadius: 12, padding: '10px 16px',
  },
  staffAvatar: {
    width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 14,
  },
  footer: {
    padding: '12px 40px', display: 'flex', justifyContent: 'space-between',
    fontSize: 12,
  },
};

export default TVDisplay;
